/**
 * @fileoverview Component for sharing an assignment.
 * This component includes a form for sharing an assignment and a button for confirming the creation.
 */

import { PlayCircleIcon } from '@heroicons/react/20/solid'
import { LinkIcon, XMarkIcon } from '@heroicons/react/24/outline'
import CopyLinkIcon from '../../../assets/share/copy-link.svg'
import EmailIcon from '../../../assets/share/email.svg'
import QRIcon from '../../../assets/share/qr.svg'
import SMSIcon from '../../../assets/share/sms.svg'
import { useModal } from '../../../hooks/useModal'
import { useToast } from '../../../hooks/useToast'
import { useGetInviteByIdQuery } from '../../../services/invites'
import {
    useNotifyParticipantByEmailMutation,
    useNotifyParticipantByPhoneMutation,
} from '../../../services/participants'
import { ShareButton } from '../../ShareButton'
import { LaunchTest } from './LaunchTest'
import { QRCode } from './QRCode'

/**
 * @interface ShareAssignmentProps
 * @description Props for the ShareAssignment component.
 * @property {string} invitationId - The ID of the invitation.
 * @property {string} participantId - The ID of the participant.
 * @property {boolean} isPhoneVerified - Whether the phone number is verified.
 * @property {boolean} isEmailVerified - Whether the email address is verified.
 */
interface ShareAssignmentProps {
    invitationId: string
    participantId: string
    isPhoneVerified: boolean
    isEmailVerified: boolean
}

/**
 * @function ShareAssignment
 * @description Component for sharing an assignment.
 * @param {ShareAssignmentProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the form for sharing an assignment.
 */
export function ShareAssignment({
    invitationId,
    participantId,
    isPhoneVerified,
    isEmailVerified,
}: ShareAssignmentProps) {
    const [open, close] = useModal()
    const [openToast] = useToast()
    const [notifyBySMS] = useNotifyParticipantByPhoneMutation()
    const [notifyByEmail] = useNotifyParticipantByEmailMutation()

    const { data: invitation, isLoading } = useGetInviteByIdQuery(invitationId)

    if (isLoading) {
        return null
    }

    function onCopyLink() {
        navigator.clipboard.writeText(invitation.link)
        openToast({
            title: 'Link copied!',
            icon: <LinkIcon className="h-5 w-5 text-indigo-500" aria-hidden="true" />,
        })
    }

    async function onShareViaEmail() {
        await notifyByEmail({
            participantId,
            invitationId,
        })

        openToast({
            title: 'Email sent!',
            icon: <EmailIcon className="fill-indigo-500" aria-hidden="true" />,
        })
    }

    async function onShareViaSMS() {
        await notifyBySMS({
            participantId,
            invitationId,
        })

        openToast({
            title: 'SMS sent!',
            icon: <SMSIcon className="fill-indigo-500" aria-hidden="true" />,
        })
    }

    function onOpenQR() {
        open({
            content: (
                <QRCode
                    invitation={invitation}
                    onBack={() => {
                        open({
                            content: (
                                <ShareAssignment
                                    invitationId={invitation.id}
                                    participantId={participantId}
                                    isPhoneVerified={isPhoneVerified}
                                    isEmailVerified={isEmailVerified}
                                />
                            ),
                        })
                    }}
                />
            ),
        })
    }

    function onOpenLink() {
        open({
            content: (
                <LaunchTest
                    invitation={invitation}
                    participantId={participantId}
                    isPhoneVerified={isPhoneVerified}
                    isEmailVerified={isEmailVerified}
                />
            ),
        })
    }

    return (
        <div className="w-[416px] h-[436px]">
            <div className="flex justify-between items-center gap-4 mb-6">
                <div className="flex">
                    <h2 className="block font-semibold text-xl text-gray-900">Test details</h2>
                </div>
                <button className="w-6 h-6" onClick={close}>
                    <XMarkIcon />
                </button>
            </div>
            <div>
                <div className="mb-4">
                    <p className="font-medium text-base text-gray-900 mb-2">Share link</p>
                    <span className="text-sm font-normal text-gray-500">
                        Send a link to the participant so they can take the test on their device.
                    </span>
                </div>
                <div className="flex mb-4">
                    <input
                        className="mr-2 flex flex-grow rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-500 shadow-sm disabled:bg-gray-100"
                        value={invitation.link}
                        readOnly
                        disabled
                    />
                </div>

                <div className="flex gap-x-2 mb-8">
                    <ShareButton
                        disabledHint="Provide email address for this participant to be able to share a test link via email."
                        disableAfterClick
                        isDisabled={!isEmailVerified}
                        onClick={onShareViaEmail}
                        Icon={EmailIcon}
                    >
                        Via Email
                    </ShareButton>

                    <ShareButton
                        disabledHint="Provide phone number for this participant to be able to share a test link via SMS."
                        disableAfterClick
                        isDisabled={!isPhoneVerified}
                        onClick={onShareViaSMS}
                        Icon={SMSIcon}
                    >
                        Via SMS
                    </ShareButton>
                    <ShareButton Icon={CopyLinkIcon} onClick={onCopyLink}>
                        Copy Link
                    </ShareButton>
                    <ShareButton Icon={QRIcon} onClick={onOpenQR}>
                        Show QR
                    </ShareButton>
                </div>

                <div className="relative mb-7 mt-7">
                    <div className="flex absolute w-full border-b border-gray-200"></div>
                    <div className="relative items-center justify-center flex">
                        <span className="absolute bg-white text-gray-500 p-2 text-xs self-center">
                            or
                        </span>
                    </div>
                </div>

                <div>
                    <div className="mb-4">
                        <p className="font-medium text-base text-gray-900 mb-2">Launch test</p>
                        <span className="text-sm font-normal text-gray-500">
                            Launch the test on this device immediately
                        </span>
                    </div>

                    <button
                        onClick={onOpenLink}
                        className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all"
                    >
                        <PlayCircleIcon className="w-5 h-5 mr-2" />
                        <span>Launch Test</span>
                    </button>
                </div>
            </div>
        </div>
    )
}
